/* eslint-disable no-class/no-class */
/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable import/no-named-as-default-member */
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable max-lines */
import { AbacRuleName } from '@jupiterone/jupiter-types';
import jwtDecode from 'jwt-decode';
import queryString from 'query-string';
import {
  getAccounts,
  getPolicyForAccount,
} from '../clients/accountClient-deprecated';
import {
  EInvitationStatus,
  IAccount,
  ISession,
  ISessionManagerOptions,
} from '../types';
import urls from '../urls';
import { getOriginUri } from '../util/url';
import abacEnforce from './abacEnforce';
import { fetchUrlEncoded } from './fetchUrlEncoded';
import findAccountInAccounts from './findAccountInAccounts';
import { findInitialAccount } from './findInitialAccount';
import retryIfTimeout from './retryIfTimeout';

/**
 * Manages storing/fetching a session object from some storage mechanism
 * (e.g. browser localStorage)
 *
 * Example session:
 *
 * {
 *   accessToken: '...',
 *   refreshToken: '...',
 *   clientId: '...',
 *   idToken: '...',
 *   email: '...',
 *   username: '...',
 *   account: { ... },
 *   accounts: [{
 *     id: '',
 *     name: '',
 *     owner: '',
 *     status: 'ACTIVE',
 *     type: 'PAID'
 *   }],
 *   policy: { ... },
 *   expiresAt: 123
 * }
 */
export default class SessionManager {
  _storageKey: string;
  _onUpdateSession: (session: ISession) => void;
  _onLogout: (gotoURL: string) => void;
  _onAccountSet: (accountId: string, subdomain?: string) => void;
  _onError: (err: any) => void;

  _getAuthRedirectUri: () => string;
  _getOauthBaseUri: () => string;
  _getTokenUrl: () => string;
  _getAccountsUrl: () => string;
  _getLoginUrl: () => string;
  _getLogoutUrl: () => string;
  _getVanityAccountId: () => string | undefined;

  _getItem: (key: string) => any;
  _setItem: (key: string, val: any) => void;
  _removeItem: (key: string) => void;

  _authTokenVerifyRefreshInterval: number;
  _authRefreshWindowMinutes: number;
  _client: any;

  /**
   * Interval timer for refreshing the auth token
   */
  _authTokenRefreshTimer: any = null;

  constructor(options: ISessionManagerOptions) {
    const {
      storageKey,
      onUpdateSession,
      setItem,
      getItem,
      removeItem,
      onLogout,
      onError,
      onAccountSet,
      authTokenVerifyRefreshInterval,
      authRefreshWindowMinutes,
      getAuthRedirectUri,
      getOauthBaseUri,
      getLoginUrl,
      getLogoutUrl,
      getTokenUrl,
      getAccountsUrl,
      getVanityAccountId,
    } = options;

    /**
     * Key in local storage where the session data is stored
     */
    this._storageKey = storageKey;
    /**
     * Called when a session is updated
     */
    this._onUpdateSession = onUpdateSession;
    /**
     * Called when a cleaning up a session for logout has finished
     */
    this._onLogout = onLogout;
    /**
     * Called when an error occurs (e.g. failed to refresh access token)
     */
    this._onError = onError;
    /**
     * Called when the account has been set in local storage
     */
    this._onAccountSet = onAccountSet;
    /**
     * Setting an item in storage (e.g. window.localStorage.setItem(k, v))
     */
    this._setItem = setItem;
    /**
     * Getting an item in storage (e.g. window.localStorage.getItem(k))
     */
    this._getItem = getItem;
    /**
     * Removing an item in storage (e.g. window.localStorage.removeItem(k))
     */
    this._removeItem = removeItem;
    /**
     * Frequency in milliseconds when the auth token gets checked for whether
     * it has expired or not
     */
    this._authTokenVerifyRefreshInterval =
      authTokenVerifyRefreshInterval || 30 * 1000;
    /**
     * Window of time before the token is required to be refreshed
     */
    this._authRefreshWindowMinutes = authRefreshWindowMinutes || 5;
    /**
     * URI that the client is redirected to after logging in
     */
    this._getAuthRedirectUri = getAuthRedirectUri;
    /**
     * Base URI of where the OAuth provider (e.g. blah.us-east-1.amazoncognito.com)
     */
    this._getOauthBaseUri = getOauthBaseUri;
    /**
     * URL for where the token should be refreshed
     */
    this._getTokenUrl = getTokenUrl;
    /**
     * URL where the accounts associated with this user should be fetched from
     */
    this._getAccountsUrl = getAccountsUrl;
    /**
     * Returns the login url to be redirected to if no session is found
     */
    this._getLoginUrl = getLoginUrl;
    /**
     * Returns the logout url to be redirected to when logging out
     */
    this._getLogoutUrl = getLogoutUrl;
    /**
     * Get initial account Id from a vanity URL if one exists
     */
    this._getVanityAccountId = getVanityAccountId;
    window.addEventListener('focus', this.shouldTokenRefresh.bind(this));
  }

  useAccount(subdomain: string) {
    const sessionData = this._getSessionFromStorage();
    const accounts = sessionData.accounts.filter(
      (acct) => acct.subdomain === subdomain
    );
    const account = accounts[0] || sessionData.accounts[0];
    this._setSessionInStorage({ ...sessionData, account });
    this._onAccountSet(account.id, account.subdomain);
  }

  /**
   * Destroys the session in storage, stops the token refresh interval, and
   * calls the `onLogout` method with a redirect URL
   */
  logout(): any {
    const sessionData = this._getSessionFromStorage();

    if (!sessionData) {
      this._onLogout(this._getLoginUrl());
      return;
    }
    this._removeItem(this._storageKey);
    this._onLogout(this._getLogoutUrl());
  }

  /**
   * @return {Boolean} - True if the token should be refreshed, otherwise false.
   */
  shouldTokenBeRefreshed(
    expiresAt: number,
    minutes: number = this._authRefreshWindowMinutes
  ): any {
    // If the token will expire in less then x minutes then go ahead and get a new one now
    return expiresAt && expiresAt - Date.now() <= minutes * 60 * 1000;
  }

  /**
   * Start the interval for refreshing the access token. The interval uses the
   * `authTokenVerifyRefreshInterval` property passed to the constructor or uses
   * a 30 second default
   */
  startTokenRefreshInterval(): any {
    this.stopTokenRefreshInterval();
    this._authTokenRefreshTimer = setInterval(() => {
      this.shouldTokenRefresh();
    }, this._authTokenVerifyRefreshInterval);
  }

  async shouldTokenRefresh() {
    const session = this._getSessionFromStorage();
    if (!session) return this.logout();
    if (this.shouldTokenBeRefreshed(session.expiresAt)) {
      try {
        let newSession: ISession = await this.refreshSessionWithAccessToken();
        this._onUpdateSession(newSession);
      } catch (err) {
        return this._onError(err);
      }
    }
  }

  /**
   * Clears the token refresh interval
   */
  stopTokenRefreshInterval(): void {
    if (this._authTokenRefreshTimer) {
      clearInterval(this._authTokenRefreshTimer);
      this._authTokenRefreshTimer = null;
    }
  }

  /**
   * Refreshes the session using access token
   *
   * @return {Object} session
   */
  async refreshSessionWithAccessToken(): Promise<any> {
    const session = this._getSessionFromStorage();

    if (!session) {
      this._onError(
        new Error('Error refreshing access token. No local session found')
      );
      return this.logout();
    }

    // refreshToken is no longer required on ISession. Casting for backwards compatibility
    const refreshedAccessTokenResponse = await this.fetchAccessTokenWithRefreshToken(
      session.refreshToken as string,
      session.clientId
    );

    const refreshedAccessTokenSession = this._buildSessionFromAuthResponse(
      refreshedAccessTokenResponse
    );

    const newSession = {
      ...session,
      ...refreshedAccessTokenSession,
    };

    this._setSessionInStorage(newSession);

    return newSession;
  }

  async _fetchInvitations(options: {
    username: string;
    accessToken: string;
  }): Promise<any> {
    return retryIfTimeout(() => {
      return fetch(
        `${urls.invitations}?pageSize=5000&user=${options.username}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${options.accessToken}`,
          },
        }
      );
    });
  }

  _redirect(url: string) {
    window.location.assign(url);
  }

  /**
   * @description checks if the operation is allowed by the user
   * @param operation The operation to check
   * @param policy The resolved policy to check against
   */
  enforce(operation: AbacRuleName, policy: any): boolean {
    return abacEnforce(operation, policy);
  }
  /**
   * Fetches a new access token with a refresh token
   *
   * @param {String} refreshToken
   * @param {String} oauthClientId
   */
  async fetchAccessTokenWithRefreshToken(
    refreshToken: string,
    oauthClientId: string
  ): Promise<any> {
    return retryIfTimeout(() => {
      return fetchUrlEncoded(this._getTokenUrl(), {
        body: {
          grant_type: 'refresh_token',
          client_id: oauthClientId,
          redirect_uri: this._getAuthRedirectUri(),
          refresh_token: refreshToken,
        },
      } as any);
    });
  }

  /**
   * Fetches an access token with a oauth code
   *
   * @param {String} code
   * @param {String} oauthClientId
   */
  async fetchAccessTokenWithCode(
    code: string,
    oauthClientId: string
  ): Promise<any> {
    return retryIfTimeout(() => {
      return fetchUrlEncoded(this._getTokenUrl(), {
        body: {
          grant_type: 'authorization_code',
          client_id: oauthClientId,
          redirect_uri: this._getAuthRedirectUri(),
          code,
        },
      } as any);
    });
  }

  /**
   * Attempts to fetch the session state from storage, refreshes an access token
   * if one exists, or logs out with the proper redirect if no session is found
   */
  /* eslint-disable complexity */
  async getSessionState(
    code: string | undefined,
    clientId: string | undefined
  ): Promise<any> {
    // If the user is using a vanity URL (e.g. https://j1dev.apps.dev.jupiterone.io),
    // we always use the account from the URL even if they already have an
    // "account" property in their session data.
    const vanityAccountId: string | undefined = this._getVanityAccountId();
    let sessionData: ISession;

    if (code && clientId) {
      let fetchAccessTokenResult;
      try {
        fetchAccessTokenResult = await this.fetchAccessTokenWithCode(
          code,
          clientId
        );
      } catch (err) {
        this._onError(err);
        return;
      }

      sessionData = this._buildSessionFromAuthResponse(fetchAccessTokenResult);
      sessionData.clientId = clientId;

      let accounts;

      try {
        const accountsResult = await getAccounts(sessionData.accessToken);
        accounts = sessionData.accounts = accountsResult.accounts;
      } catch (err) {
        this._onError(err);
        return;
      }

      if (!accounts || !accounts.length) {
        // Set the session in storage right away then redirect
        this._setSessionInStorage(sessionData);
      } else {
        const initialAccount: IAccount | undefined = this._getInitialAccount(
          accounts,
          vanityAccountId
        );

        if (!initialAccount) {
          // Check if the user has an invitation to the given account and redirect
          // as necessary
          const { items: invitations } = await this._fetchInvitations({
            username: sessionData.username,
            accessToken: sessionData.accessToken,
          });
          const hasPendingAccountInvite = (invite: any) =>
            invite.accountSubdomain &&
            invite.accountSubdomain === vanityAccountId &&
            invite.status === EInvitationStatus.pending;
          const hasInviteToAccount = invitations.some(hasPendingAccountInvite);

          this._redirect(
            hasInviteToAccount
              ? `${urls.jupiteroneUrl}/landing?settings=invitations`
              : `${urls.jupiteroneUrl}/landing?error=unauthorizedAccountAccess&account=${vanityAccountId}`
          );
          return sessionData;
        }
        const { policy } = await getPolicyForAccount(
          sessionData.accessToken,
          initialAccount.id
        );
        sessionData.policy = policy;
        sessionData.account = initialAccount;
        this._setSessionInStorage(sessionData);
      }

      // Remove the querystring params from the URL after a successful login
      // preserving `?query=...` from the destination
      // so that the landing page can run the j1 query on load
      const { destination = '' } = queryString.parse(window.location.search);

      const {
        query: j1QueryParam,
        question: j1QuestionParam,
        encodedParams: j1EncodedParams,
      } = queryString.parse(destination as string);

      const query = queryString.stringify({
        query: j1QueryParam,
        question: j1QuestionParam,
        encodedParams: j1EncodedParams,
      });
      const url =
        getOriginUri() +
        window.location.pathname +
        `${query ? `?${query}` : ''}`;
      window.history.pushState({}, '', url);
    } else {
      sessionData = this._getSessionFromStorage();

      if (!sessionData) {
        this.logout();
        return;
      } else if (this.shouldTokenBeRefreshed(sessionData.expiresAt)) {
        try {
          sessionData = await this.refreshSessionWithAccessToken();
        } catch (err) {
          this._onError(err);
          return;
        }
      }

      // Every time the page loads, if there is an existing session, re-fetch
      // the accounts so they're accurate. This is especially important for the
      // case that the user was redirected to the accounts app to accept an
      // invitation. After they accept the invitation, they should be able to
      // navigate back to any JupiterOne app and have their new account show up.
      // This will also update the user's accounts if they've been removed from
      // an account since the last time they loaded the page.
      try {
        const accountsResult = await getAccounts(sessionData.accessToken);
        const accounts = (sessionData.accounts = accountsResult.accounts);
        const currentlySelectedAccount = findAccountInAccounts(
          accounts,
          sessionData.account
        );

        if (!accounts || !accounts.length) {
          // There are no accounts associated with this user. Reset their local
          // account data because it may contain stale account data.
          sessionData.accounts = [];
          delete sessionData.account;
          delete (sessionData as Partial<ISession>).policy;

          this._setSessionInStorage(sessionData);
        } else if (
          !vanityAccountId &&
          sessionData.account &&
          currentlySelectedAccount
        ) {
          // If there is already an active account in local storage and they
          // weren't removed from that account since the last time the page
          // was loaded, we just keep using that account.
          const { policy } = await getPolicyForAccount(
            sessionData.accessToken,
            currentlySelectedAccount.id
          );
          sessionData.policy = policy;
          // update the set account as we did the accounts incase
          // a property inside changed, E.G. status.
          sessionData.account = currentlySelectedAccount;
          this._setSessionInStorage(sessionData);
        } else {
          const initialAccount: IAccount | undefined = this._getInitialAccount(
            accounts,
            vanityAccountId
          );

          if (!initialAccount) {
            // TODO: If this account is not found, we should redirect to the
            // logout URL without the account Id in it. This user is not
            // member of this account.
            this.logout();
            return;
          }
          const { policy } = await getPolicyForAccount(
            sessionData.accessToken,
            initialAccount.id
          );
          sessionData.policy = policy;
          sessionData.account = initialAccount;
          this._setSessionInStorage(sessionData);
        }
      } catch (err) {
        this._onError(err);
        return;
      }
    }

    if (!this._authTokenRefreshTimer) {
      this.startTokenRefreshInterval();
    }

    return sessionData;
  }

  _getInitialAccount(accounts: IAccount[], vanityAccountId?: string) {
    let account: IAccount | undefined;

    if (vanityAccountId) {
      account = findAccountInAccounts(accounts, vanityAccountId);
    } else {
      // If there was no active account in local storage or in the URL,
      // we select the first active account or the first account if none
      // are active.
      //
      // TODO: Consider throwing an error and bubbling it up to the client
      // if no accounts are active.
      account = findInitialAccount(accounts);
    }

    return account;
  }

  /**
   * Builds a session from an auth response
   *
   * @param {Object} response - Response from auth service
   * @return {Object} session - Session object to be set in storage
   */
  _buildSessionFromAuthResponse(response: any): any {
    const {
      access_token: accessToken,
      id_token: idToken,
      expires_in: expiresIn,
      refresh_token: refreshToken,
      error,
    } = response;

    if (error) {
      this._onError(new Error(`Auth response error: "${error.toString()}"`));

      return;
    }

    const session: ISession = {} as ISession;

    // Session properties can come back with "undefined" values. We do not want
    // to override existing data in our local session with "undefined"
    if (accessToken) session.accessToken = accessToken;
    if (refreshToken) session.refreshToken = refreshToken;

    if (idToken) {
      session.idToken = idToken;

      const decoded: any = jwtDecode(idToken);
      session.email = decoded.email;
      session.username = decoded['cognito:username'];
    }

    if (expiresIn) {
      const radix: number = 10;
      session.expiresAt =
        new Date().getTime() + parseInt(expiresIn, radix) * 1000;
    }

    return session;
  }

  /**
   * Helper function for fetching from the storage with the correct key
   */
  _getSessionFromStorage(): ISession {
    return this._getItem(this._storageKey);
  }

  /**
   * Helper function for setting in the storage with the correct key
   */
  _setSessionInStorage(session: ISession) {
    return this._setItem(this._storageKey, session);
  }
}
